import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl } from '@angular/forms';
import { finalize } from 'rxjs';
import { MaxxSlackChannel } from '../maxx-settings.model';
import { MaxxSettingsService, maxxSettingsErrorMessage } from '../maxx-settings.service';

@Component({
  selector: 'pdq-maxx-slack-settings',
  templateUrl: './maxx-slack-settings.component.html',
  styleUrls: ['./maxx-slack-settings.component.scss'],
  standalone: false,
})
export class MaxxSlackSettingsComponent implements OnInit {
  private readonly maxxSettingsService = inject(MaxxSettingsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly channelIdControl = new UntypedFormControl('');
  readonly connectedChannel = signal<MaxxSlackChannel | null>(null);
  readonly checkedChannel = signal<MaxxSlackChannel | null>(null);
  readonly isLoading = signal(true);
  readonly loadFailed = signal(false);
  readonly isEditing = signal(false);
  readonly isChecking = signal(false);
  readonly isConnecting = signal(false);
  readonly isDisconnecting = signal(false);
  readonly errorMessage = signal<string | null>(null);

  ngOnInit(): void {
    this.maxxSettingsService
      .getSlackChannel()
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (channel) => this.connectedChannel.set(channel),
        error: () => this.loadFailed.set(true),
      });

    this.channelIdControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.checkedChannel.set(null);
      this.errorMessage.set(null);
    });
  }

  startEditing(): void {
    this.channelIdControl.setValue('');
    this.isEditing.set(true);
  }

  check(): void {
    const channelId = String(this.channelIdControl.value ?? '').trim();
    if (!channelId || this.isChecking()) return;

    this.isChecking.set(true);
    this.maxxSettingsService
      .checkSlackChannel(channelId)
      .pipe(
        finalize(() => this.isChecking.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (channel) => {
          if (String(this.channelIdControl.value ?? '').trim() === channelId) this.checkedChannel.set(channel);
        },
        error: (error) => this.errorMessage.set(maxxSettingsErrorMessage(error)),
      });
  }

  connect(): void {
    const channel = this.checkedChannel();
    if (!channel || this.isConnecting()) return;

    this.isConnecting.set(true);
    this.maxxSettingsService
      .connectSlackChannel(channel.id)
      .pipe(
        finalize(() => this.isConnecting.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (connected) => {
          this.connectedChannel.set(connected);
          this.isEditing.set(false);
          this.channelIdControl.setValue('');
        },
        error: (error) => this.errorMessage.set(maxxSettingsErrorMessage(error)),
      });
  }

  disconnect(): void {
    if (this.isDisconnecting()) return;

    this.isDisconnecting.set(true);
    this.errorMessage.set(null);
    this.maxxSettingsService
      .disconnectSlackChannel()
      .pipe(
        finalize(() => this.isDisconnecting.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.connectedChannel.set(null),
        error: (error) => this.errorMessage.set(maxxSettingsErrorMessage(error)),
      });
  }
}
